import { type HTMLAttributes, type ReactNode, forwardRef, useEffect, useRef, useCallback } from 'react';

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  ({ open, onClose, title, children, className = '', ...props }, ref) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const previousFocusRef = useRef<HTMLElement | null>(null);

    const handleKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
          return;
        }

        if (e.key === 'Tab' && dialogRef.current) {
          const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstFocusable = focusableElements[0];
          const lastFocusable = focusableElements[focusableElements.length - 1];

          if (e.shiftKey) {
            if (document.activeElement === firstFocusable) {
              e.preventDefault();
              lastFocusable?.focus();
            }
          } else {
            if (document.activeElement === lastFocusable) {
              e.preventDefault();
              firstFocusable?.focus();
            }
          }
        }
      },
      [onClose]
    );

    useEffect(() => {
      if (open) {
        previousFocusRef.current = document.activeElement as HTMLElement;
        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';

        // Focus the dialog or first focusable element
        requestAnimationFrame(() => {
          const focusable = dialogRef.current?.querySelector<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          focusable?.focus();
        });
      } else {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
        previousFocusRef.current?.focus();
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
      };
    }, [open, handleKeyDown]);

    if (!open) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        role="presentation"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-text-primary/30 backdrop-blur-sm animate-fade-in"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Dialog */}
        <div
          ref={(node) => {
            (dialogRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? 'modal-title' : undefined}
          className={[
            'relative z-10 w-full max-w-lg mx-4',
            'bg-bg-card rounded-md shadow-modal border border-border',
            'animate-scale-in',
            className,
          ].join(' ')}
          {...props}
        >
          {/* Header with title and close button */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            {title && (
              <h2 id="modal-title" className="text-heading-3 font-semibold text-text-heading">
                {title}
              </h2>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close modal"
              className={[
                'ml-auto p-1.5 rounded-sm text-text-secondary',
                'transition-all duration-fast ease-fast',
                'hover:bg-hover-bg hover:text-text-primary',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
              ].join(' ')}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M15 5L5 15" />
                <path d="M5 5L15 15" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-5 py-4">{children}</div>
        </div>
      </div>
    );
  }
);

Modal.displayName = 'Modal';
